import React, { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { PieChart } from 'lucide-react';
import { useTheme } from '@mui/material/styles';
import './Candidates.css';

ChartJS.register(ArcElement, Tooltip, Legend);

// Helper to filter out zero values and align labels/colors
const filterZeroValues = (labels, data, backgroundColor) => {
  const filtered = labels
    .map((label, i) => ({ label, value: data[i], color: backgroundColor[i] }))
    .filter(item => item.value > 0);

  return {
    labels: filtered.map(item => item.label),
    data: filtered.map(item => item.value),
    backgroundColor: filtered.map(item => item.color),
  };
};

const CandidateInterviewAnalyticsDashboard = ({
  interviewsData,
  candidateId,
  roundOptions,
  interviewStatusOptions,
  locationOptions,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const filteredInterviews = useMemo(() => {
    return candidateId
      ? interviewsData.filter((interview) => interview.candidateId === candidateId)
      : interviewsData;
  }, [interviewsData, candidateId]);

  // Interview Rounds Distribution
  const roundsData = useMemo(() => {
    const roundCounts = {
      FIRST: 0,
      SECOND: 0,
      THIRD: 0,
    };
    filteredInterviews.forEach((interview) => {
      if (roundCounts.hasOwnProperty(interview.round)) {
        roundCounts[interview.round]++;
      }
    });

    const rawLabels = roundOptions.map((opt) => opt.label);
    const rawData = roundOptions.map((opt) => roundCounts[opt.value] || 0);
    const rawColors = ['#3b82f6', '#10b981', '#f59e0b']; // Tailwind: blue-500, emerald-500, amber-500

    const { labels, data, backgroundColor } = filterZeroValues(rawLabels, rawData, rawColors);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 12,
        },
      ],
    };
  }, [filteredInterviews, roundOptions]);

  // Location Distribution
  const locationData = useMemo(() => {
    const locationCounts = {
      NADOR: 0,
      TANGIER: 0,
      ONLINE: 0,
    };
    filteredInterviews.forEach((interview) => {
      if (locationCounts.hasOwnProperty(interview.location)) {
        locationCounts[interview.location]++;
      }
    });

    const rawLabels = locationOptions;
    const rawData = locationOptions.map((loc) => locationCounts[loc] || 0);
    const rawColors = ['#8b5cf6', '#ec4899', '#06b6d4']; // violet-500, pink-500, cyan-500

    const { labels, data, backgroundColor } = filterZeroValues(rawLabels, rawData, rawColors);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 12,
        },
      ],
    };
  }, [filteredInterviews, locationOptions]);

  // Interview Status Distribution
  const statusData = useMemo(() => {
    const statusCounts = {
      SCHEDULED: 0,
      COMPLETED: 0,
      CANCELLED: 0,
    };
    filteredInterviews.forEach((interview) => {
      if (statusCounts.hasOwnProperty(interview.status)) {
        statusCounts[interview.status]++;
      }
    });

    const rawLabels = interviewStatusOptions.map((opt) => opt.label);
    const rawData = interviewStatusOptions.map((opt) => statusCounts[opt.value] || 0);
    const rawColors = ['#f97316', '#22c55e', '#ef4444']; // orange-500, green-500, red-500

    const { labels, data, backgroundColor } = filterZeroValues(rawLabels, rawData, rawColors);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor,
          borderColor: '#ffffff',
          borderWidth: 2,
          hoverOffset: 12,
        },
      ],
    };
  }, [filteredInterviews, interviewStatusOptions]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 11 },
          color: isDarkMode ? '#e5e7eb' : '#374151',
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: isDarkMode ? '#374151' : '#1f2937',
        titleColor: '#fff',
        bodyColor: '#f9fafb',
        titleFont: { size: 12 },
        bodyFont: { size: 11 },
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label: (context) => `${context.label}: ${context.raw}`,
        },
      },
    },
    cutout: '65%',
    animation: {
      animateScale: true,
      animateRotate: true,
      duration: 800,
    },
  };

  const hasData = filteredInterviews.length > 0;

  return (
    <div
      className="charts-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: '1.25rem',
        marginTop: '1.25rem',
      }}
    >
      {/* Interview Rounds Distribution */}
      <div className={`card status-distribution-card ${isDarkMode ? 'dark-mode-card' : ''}`} style={{ backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff', border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gray-200)' }}>
        <div className="chart-header">
          <div>
            <h3 className="chart-title" style={{ color: isDarkMode ? '#f9fafb' : 'inherit' }}>Interview Rounds Distribution</h3>
            <p className="chart-subtitle" style={{ color: isDarkMode ? '#9ca3af' : 'inherit' }}>
              {candidateId ? 'Candidate-specific rounds' : 'All interviews by round'}
            </p>
          </div>
          <PieChart className="chart-icon" size={18} color={isDarkMode ? '#9ca3af' : 'inherit'} />
        </div>
        <div className="chart-content" style={{ height: '300px' }}>
          {hasData && roundsData.labels.length > 0 ? (
            <Doughnut data={roundsData} options={chartOptions} />
          ) : (
            <p className="empty-message">No interview data available</p>
          )}
        </div>
      </div>

      {/* Location Distribution */}
      <div className={`card status-distribution-card ${isDarkMode ? 'dark-mode-card' : ''}`} style={{ backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff', border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gray-200)' }}>
        <div className="chart-header">
          <div>
            <h3 className="chart-title" style={{ color: isDarkMode ? '#f9fafb' : 'inherit' }}>Location Distribution</h3>
            <p className="chart-subtitle" style={{ color: isDarkMode ? '#9ca3af' : 'inherit' }}>
              {candidateId ? 'Candidate interview locations' : 'All interviews by location'}
            </p>
          </div>
          <PieChart className="chart-icon" size={18} color={isDarkMode ? '#9ca3af' : 'inherit'} />
        </div>
        <div className="chart-content" style={{ height: '300px' }}>
          {hasData && locationData.labels.length > 0 ? (
            <Doughnut data={locationData} options={chartOptions} />
          ) : (
            <p className="empty-message">No interview data available</p>
          )}
        </div>
      </div>

      {/* Interview Status Distribution */}
      <div className={`card status-distribution-card ${isDarkMode ? 'dark-mode-card' : ''}`} style={{ backgroundColor: isDarkMode ? 'rgba(31, 41, 55, 0.5)' : '#ffffff', border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid var(--gray-200)' }}>
        <div className="chart-header">
          <div>
            <h3 className="chart-title" style={{ color: isDarkMode ? '#f9fafb' : 'inherit' }}>Interview Status Distribution</h3>
            <p className="chart-subtitle" style={{ color: isDarkMode ? '#9ca3af' : 'inherit' }}>
              {candidateId ? 'Candidate interview statuses' : 'All interviews by status'}
            </p>
          </div>
          <PieChart className="chart-icon" size={18} color={isDarkMode ? '#9ca3af' : 'inherit'} />
        </div>
        <div className="chart-content" style={{ height: '300px' }}>
          {hasData && statusData.labels.length > 0 ? (
            <Doughnut data={statusData} options={chartOptions} />
          ) : (
            <p className="empty-message">No interview data available</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CandidateInterviewAnalyticsDashboard;